"use client";

import { Check, Share2 } from "lucide-react";
import { useState } from "react";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { FloatingSticker } from "@/components/wallet/sticker-card-3d";
import {
  stickerCloseness,
  stickerTone,
  withAlpha,
} from "@/components/wallet/sticker-display";
import {
  getStickerProgress,
  stickerRequirement,
} from "@/components/wallet/wallet-stickers";
import { useAuthState } from "@/hooks/use-auth-state";
import { useMyStickers } from "@/hooks/use-stickers";
import { notificationLinks } from "@/lib/notification-links";
import { cn } from "@/lib/utils";
import { findSticker } from "@/lib/wallet/sticker-share";
import { StickerPostComposer } from "./sticker-post-composer";

export interface StickerOwner {
  id: string;
  name: string;
}

/**
 * Someone's shared sticker, up close: the same floating card as the wallet,
 * whose collection it is, and whether the viewer has it too (and how close
 * they are if not — their own numbers, never the owner's).
 */
export function SharedStickerSheet({
  open,
  onOpenChange,
  stickerId,
  owner,
  viewerId: viewerIdProp,
  layer,
  nested = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stickerId: string;
  owner: StickerOwner | null;
  /** Defaults to the signed-in session's id. */
  viewerId?: string | null;
  layer?: number;
  nested?: boolean;
}) {
  const sticker = findSticker(stickerId);
  const mine = useMyStickers(open);
  const { identity } = useAuthState();
  const viewerId = viewerIdProp ?? identity?.id ?? null;
  const [composerOpen, setComposerOpen] = useState(false);
  if (!sticker) return null;

  const isOwner = Boolean(owner && viewerId && owner.id === viewerId);
  const progress = mine.data
    ? getStickerProgress(sticker, mine.data.stats)
    : null;
  const tone = stickerTone(sticker);
  const firstName = owner?.name.split(" ")[0] ?? "Someone";

  const shareMine = () => {
    onOpenChange(false);
    // Let this sheet leave before the composer rises.
    setTimeout(() => setComposerOpen(true), 220);
  };

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} nested={nested}>
        <DrawerContent
          layer={layer}
          className="mx-auto max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:rounded-t-3xl"
          onClick={(event) => event.stopPropagation()}
        >
          <DrawerTitle className="sr-only">
            Hiive sticker: {sticker.title}
          </DrawerTitle>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-72"
            style={{
              background: `radial-gradient(70% 70% at 50% 30%, ${withAlpha(tone.to, 0.28)}, ${withAlpha(tone.to, 0)} 72%)`,
            }}
          />
          <div className="relative flex flex-col items-center px-5 pt-2 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
            <FloatingSticker
              sticker={sticker}
              width={216}
              drag
              subtitle={
                isOwner
                  ? "your collection"
                  : owner
                    ? `${firstName}'s collection`
                    : "collection"
              }
            />

            <p className="mt-1 text-center text-[13px] text-text-secondary">
              {isOwner
                ? "From your collection"
                : owner
                  ? `From ${owner.name}'s collection`
                  : "A Hiive sticker"}{" "}
              · for reaching {stickerRequirement(sticker)}
            </p>

            <ViewerStatus
              signedIn={mine.signedIn}
              loading={mine.signedIn && mine.isPending}
              failed={mine.isError}
              isOwner={isOwner}
              unlocked={progress?.unlocked ?? false}
              remaining={progress?.remaining ?? 0}
              closeness={
                progress ? stickerCloseness(sticker, progress.current) : 0
              }
              requirement={stickerRequirement(sticker)}
            />

            <div className="mt-4 flex w-full gap-2">
              {progress?.unlocked ? (
                <button
                  type="button"
                  onClick={shareMine}
                  className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-text-inverse transition-transform duration-150 ease-out-strong active:scale-[0.97] dark:bg-brand"
                >
                  <Share2 className="size-4" strokeWidth={2.25} />
                  {isOwner ? "Share to feed" : "Share yours"}
                </button>
              ) : null}
              {mine.signedIn ? (
                <BackAwareLink
                  href={notificationLinks.walletStickers()}
                  onClick={() => onOpenChange(false)}
                  className="flex h-11 flex-1 items-center justify-center rounded-full border border-border bg-surface text-sm font-semibold text-text-primary transition-transform duration-150 ease-out-strong active:scale-[0.97]"
                >
                  My stickers
                </BackAwareLink>
              ) : null}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <StickerPostComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        stickerId={stickerId}
      />
    </>
  );
}

function ViewerStatus({
  signedIn,
  loading,
  failed,
  isOwner,
  unlocked,
  remaining,
  closeness,
  requirement,
}: {
  signedIn: boolean;
  loading: boolean;
  failed: boolean;
  isOwner: boolean;
  unlocked: boolean;
  remaining: number;
  closeness: number;
  requirement: string;
}) {
  // One fixed-height row in every state, so the sheet never jumps.
  const row =
    "mt-4 flex min-h-[3.75rem] w-full flex-col justify-center rounded-2xl border border-border bg-surface px-4 py-3";

  if (!signedIn) {
    return (
      <div className={row}>
        <p className="text-sm text-text-secondary">
          Sign in to see whether this one is in your collection.
        </p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className={row} aria-busy="true">
        <div className="h-3.5 w-40 animate-pulse rounded-full bg-black/[0.07] dark:bg-white/[0.08]" />
        <div className="mt-2 h-1.5 w-full animate-pulse rounded-full bg-black/[0.07] dark:bg-white/[0.08]" />
      </div>
    );
  }
  if (failed) {
    return (
      <div className={row}>
        <p className="text-sm text-text-secondary">
          Couldn't check your collection right now.
        </p>
      </div>
    );
  }
  if (unlocked) {
    return (
      <div className={cn(row, "flex-row items-center justify-start gap-3")}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/12 text-emerald-700 dark:text-emerald-400">
          <Check className="size-4" strokeWidth={2.5} />
        </span>
        <p className="text-sm font-semibold text-text-primary">
          {isOwner ? "It's in your collection" : "You have this one too"}
        </p>
      </div>
    );
  }
  return (
    <div className={row}>
      <p className="text-sm text-text-secondary">
        <span className="font-semibold text-text-primary">
          Not in your collection yet
        </span>{" "}
        · {remaining} more to reach {requirement}
      </p>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.07] dark:bg-white/[0.08]">
        <div
          className="h-full origin-left rounded-full bg-brand"
          style={{ transform: `scaleX(${Math.max(closeness, 0.03)})` }}
        />
      </div>
    </div>
  );
}
